import './SellerNdaTermSelectionForm.scss';
import React, {
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { Label } from 'reactstrap';
import { Feedback, Field, Form, Input } from '@availity/form';
import * as yup from 'yup';
import { useStateSelector } from '../../../../store/selectors';
import { MarketplaceDocumentSectionFieldValidator } from '../DocumentSectionField/MarketplaceDocumentSectionFieldValidator';
import { MarketplaceDocumentSectionField } from '../DocumentSectionField/MarketplaceDocumentSectionField';
import {
    defaultTimelineAction,
    TimelineAction,
} from '../../../../slices/marketplace/timeline/models/timeline-actions.model';
import {
    createDefaultDocumentDetails,
    DocumentDetails,
} from '../../../../slices/marketplace/timeline/models/document-details.model';
import { LinkedDataRequestProject } from '../../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import {
    NdaTermSelectionOptionsList,
    TimelineActionType,
} from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { DocumentSection } from '../../../../slices/marketplace/timeline/models/document-section.model';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { NdaTermSelectionDocumentTemplatesState } from '../../../../slices/marketplace/timeline/models/nda-term-selection';
import { generateGuid } from '../../../../utils/typedHelper';

interface SellerNdaTermSelectionFormProps {
    party: TimelineParty;
    onSubmit(
        action: TimelineAction,
        masterDocumentDetails?: DocumentDetails,
        additionalDocumentDetails?: DocumentDetails[],
        questionnaire?: LinkedDataRequestProject,
        ndaTermSelection?: NdaTermSelectionDocumentTemplatesState
    ): void;
    disabled: boolean;
    documentDetails?: DocumentDetails;
    documentDetailsList: DocumentDetails[];
    documentsSections: DocumentSection[];
    setDocumentSectionList(sections: DocumentSection[]): void;
    timelineEvents: TimelineEvent[];
    currentEvent: TimelineEvent;
    linkedAction?: TimelineAction;
    ndaTermSelectionState: NdaTermSelectionDocumentTemplatesState;
}

export const SellerNdaTermSelectionForm = forwardRef(
    (props: SellerNdaTermSelectionFormProps, ref) => {
        const formRef = useRef<any>();

        useImperativeHandle(ref, () => ({
            handleSubmit() {
                formRef?.current?.handleSubmit();
            },
        }));

        const documentTemplates = useStateSelector(
            (state) => state.timelines.documentTemplatesTable.templates
        );

        const [masterDocumentDetails, setMasterDocumentDetails] = useState({
            ...(props.documentDetails ?? createDefaultDocumentDetails(true)),
        });

        return (
            <div className="nda-term-selection-form">
                <Form
                    innerRef={formRef}
                    initialValues={{
                        actionType: NdaTermSelectionOptionsList.some(
                            (s) => s.value === props.linkedAction?.actionType
                        )
                            ? props.linkedAction?.actionType
                            : TimelineActionType.SellerCustomSelectDataNdaTerms,
                        noNsTemplate:
                            props.ndaTermSelectionState?.noNsTemplateId ?? 0,
                        dvmAndStaffNsTemplate:
                            props.ndaTermSelectionState
                                ?.dvmAndStaffNsTemplateId ?? 0,
                        dvmNsTemplate:
                            props.ndaTermSelectionState?.dvmNsTemplateId ?? 0,
                        staffNsTemplate:
                            props.ndaTermSelectionState?.staffNsTemplateId ?? 0,
                        documentName: masterDocumentDetails.name,
                        documentSection: masterDocumentDetails.sectionId,
                    }}
                    validationSchema={yup.object().shape({
                        actionType: yup
                            .number()
                            .uniqueGroupValidation(
                                props.timelineEvents ?? [],
                                props.currentEvent
                            )
                            .uniqueActionValidation(
                                props.timelineEvents ?? [],
                                props.currentEvent
                            ),
                        noNsTemplate: yup
                            .number()
                            .min(1, 'Template is required'),
                        dvmAndStaffNsTemplate: yup
                            .number()
                            .min(1, 'Template is required'),
                        dvmNsTemplate: yup
                            .number()
                            .min(1, 'Template is required'),
                        staffNsTemplate: yup
                            .number()
                            .min(1, 'Template is required'),
                        documentName: yup
                            .string()
                            .trim()
                            .required('Document name is required'),
                        documentSection:
                            MarketplaceDocumentSectionFieldValidator.documentSectionValidationRules(
                                props.documentsSections,
                                props.party
                            ),
                    })}
                    onSubmit={async (values) => {
                        let docDetails = masterDocumentDetails;

                        docDetails = {
                            ...docDetails,
                            name: values.documentName.trim(),
                            counterParty: TimelineParty.VetValue,
                            templateId: null,
                            sectionId: values.documentSection,
                        };
                        props.onSubmit(
                            {
                                ...defaultTimelineAction,
                                actionType: Number(values.actionType),
                                referenceItemId: docDetails.id,
                            },
                            docDetails,
                            undefined,
                            undefined,
                            {
                                id: generateGuid(),
                                noNsTemplateId: values.noNsTemplate,
                                dvmAndStaffNsTemplateId:
                                    values.dvmAndStaffNsTemplate,
                                dvmNsTemplateId: values.dvmNsTemplate,
                                staffNsTemplateId: values.staffNsTemplate,
                            }
                        );
                    }}>
                    <div className="nda-term-selection">
                        <Label for="documentTemplate">NDA Type</Label>
                        <Input
                            type="select"
                            name="actionType"
                            disabled={props.disabled}>
                            {NdaTermSelectionOptionsList.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Input>
                        <Feedback name="actionType" />
                        <MarketplaceDocumentSectionField
                            documentsSections={props.documentsSections}
                            disabled={props.disabled}
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                            documentDetailsList={props.documentDetailsList}
                            type="sellers"
                            sectionsModalMode={'built-in'}
                        />
                        <Label for="noNsTemplate">
                            Document vNoNS Template
                        </Label>
                        <Input
                            type="select"
                            name="noNsTemplate"
                            disabled={props.disabled}>
                            <option value={0}>Select</option>
                            {documentTemplates.map((option) => (
                                <option
                                    key={`template-${option.id}`}
                                    value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Input>
                        <Feedback name="noNsTemplate" />

                        <Label for="dvmAndStaffNsTemplate">
                            Document vDVM&StaffNS Template
                        </Label>
                        <Input
                            type="select"
                            name="dvmAndStaffNsTemplate"
                            disabled={props.disabled}>
                            <option value={0}>Select</option>
                            {documentTemplates.map((option) => (
                                <option
                                    key={`template-${option.id}`}
                                    value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Input>
                        <Feedback name="dvmAndStaffNsTemplate" />

                        <Label for="dvmNsTemplate">
                            Document vDVMNS Template
                        </Label>
                        <Input
                            type="select"
                            name="dvmNsTemplate"
                            disabled={props.disabled}>
                            <option value={0}>Select</option>
                            {documentTemplates.map((option) => (
                                <option
                                    key={`template-${option.id}`}
                                    value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Input>
                        <Feedback name="dvmNsTemplate" />

                        <Label for="staffNsTemplate">
                            Document vStaffNS Template
                        </Label>
                        <Input
                            type="select"
                            name="staffNsTemplate"
                            disabled={props.disabled}>
                            <option value={0}>Select</option>
                            {documentTemplates.map((option) => (
                                <option
                                    key={`template-${option.id}`}
                                    value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Input>
                        <Feedback name="staffNsTemplate" />

                        <Field
                            className="field"
                            name="documentName"
                            label="Document Name"
                            disabled={props.disabled}
                        />
                    </div>
                </Form>
            </div>
        );
    }
);
